import React, {useContext, useState, useEffect} from 'react'
import {CompositeScreenProps, useNavigation} from '@react-navigation/native'
import {ApolloError, ApolloQueryResult} from '@apollo/client'
import {StackNavigationProp, StackScreenProps} from '@react-navigation/stack'

import {aggregatorsId} from 'src/lib/user/userEnvConsts'
import {AppEvents, ManageActiveLoanEvents} from 'src/lib/Analytics/app_events'
import {BaseTemplate} from 'src/products/general/components/templates/BaseTemplate/BaseTemplate'
import {ChaosEnabled} from '@possible/chaos/src/component-chaos/components/ChaosEnabled'
import {LoanPayContext, LoanPaySetterContext} from 'src/products/loans/MakePayment/LoanPayProvider'
import {LoanPayMethodsPaymentMethod} from 'src/products/loans/MakePayment/LoanPayMethods/LoanPayMethods.types'
import {LoanPayNavigatorStack} from 'src/products/loans/MakePayment/LoanPayNavigatorStack'
import {MainStackParamList} from 'src/nav/MainStackParamsList'
import {PaymentMethodTypesEnum} from 'src/products/general/GeneralPaymentMethods/types'
import {TrackAppEvent} from 'src/lib/Analytics/analytics_compat'
import {
  trackLoansMakePaymentScreenRenderFailure,
  isAchPaymentMethod,
  isDebitCardPaymentMethod,
} from 'src/products/loans/MakePayment/LoanPay.utils'
import {useBankAggregator} from 'src/products/general/GeneralPaymentMethods/useBankAggregator'
import {useCassandraQuery} from '@possible/cassandra/src/utils/hooks'
import {usePageViewedAnalytics} from 'src/lib/Analytics/usePageViewedAnalytics'
import {usePfDispatch} from 'src/store/utils'
import {ViewPaymentMethodsTemplate} from 'src/products/loans/MakePayment/LoanPayMethods/ViewPaymentMethodsTemplate'
import Log from 'src/lib/loggingUtil'
import {
  LoanPayMethodsDocument,
  LoanPayMethodsQuery,
} from 'src/products/loans/MakePayment/LoanPayMethods/LoanPayMethods.gqls'
import {
  LoanPaymentMethod,
  LoanPaymentMethodAch,
  LoanPaymentMethodDebitCard,
} from 'src/products/loans/MakePayment/LoanPay.types'
import {
  convertAggregatorStringToType,
  logAddPaymentMethodError,
} from 'src/products/general/GeneralPaymentMethods/GeneralPaymentMethods.utils'
import {getPaymentMethodEventTypeFromEnum} from 'src/products/loans/MakePayment/LoanPayMethods/LoanPayMethods.utils'

type LoanPaymentMethodsProps = Pick<
  CompositeScreenProps<
    StackScreenProps<LoanPayNavigatorStack, 'LoanPayMethods'>,
    StackScreenProps<MainStackParamList>
  >,
  'route' | 'navigation'
>

/**
 * Screen to view all payment methods for a loan user and add new payment methods.
 */
const LoanPayMethods: React.FunctionComponent<LoanPaymentMethodsProps> = (
  props: LoanPaymentMethodsProps,
) => {
  const {navigation} = props
  const dispatch = usePfDispatch()
  const {selectedPaymentMethodId} = useContext(LoanPayContext)
  const {setSelectedPaymentMethodId} = useContext(LoanPaySetterContext)

  const mainStackNavigation = useNavigation<StackNavigationProp<MainStackParamList>>()

  const {openBankAggregator} = useBankAggregator(mainStackNavigation)

  usePageViewedAnalytics({
    eventName: ManageActiveLoanEvents.make_payment_methods_screen_viewed,
    eventCategory: AppEvents.Category.ManageActiveLoan,
  })

  const queryResult = useCassandraQuery(
    LoanPayMethodsDocument,
    {
      fetchPolicy: 'network-only',
      onError: (e: ApolloError) => {
        Log.error(e, 'LoanPayMethods query failed')
      },
    },
    (data) => {
      const eligiblePaymentMethods: LoanPayMethodsPaymentMethod[] = []
      for (const paymentMethod of data.me.paymentMethods.loanEligible ?? []) {
        if (
          paymentMethod.__typename === 'AchPaymentMethod' ||
          paymentMethod.__typename === 'DebitCardPaymentMethod'
        ) {
          eligiblePaymentMethods.push(paymentMethod)
        }
      }

      const bankingAggregatorId: aggregatorsId | undefined = convertAggregatorStringToType(
        data.getUserEnv.bankingAggregatorId,
      )

      const canAddMakePaymentInstrument: boolean = Boolean(data.me.canAddAdhocPaymentInstrument)

      return {
        paymentMethods: eligiblePaymentMethods,
        bankingAggregatorId,
        canAddMakePaymentInstrument,
      }
    },
  )

  const isYodleeAggregator = queryResult.selectedData?.bankingAggregatorId === aggregatorsId.yodlee

  const achPaymentMethods: LoanPaymentMethodAch[] =
    queryResult.selectedData?.paymentMethods.filter(isAchPaymentMethod) ?? []
  const debitPaymentMethods: LoanPaymentMethodDebitCard[] =
    queryResult.selectedData?.paymentMethods.filter(isDebitCardPaymentMethod) ?? []

  const initialSelectedPaymentMethod = queryResult.selectedData?.paymentMethods?.find(
    (method) =>
      (method.__typename === 'AchPaymentMethod' ||
        method.__typename === 'DebitCardPaymentMethod') &&
      method.bankingPaymentInstrumentId === selectedPaymentMethodId,
  )

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    LoanPaymentMethod | undefined | null
  >(initialSelectedPaymentMethod)

  useEffect(() => {
    if (initialSelectedPaymentMethod) {
      setSelectedPaymentMethod(initialSelectedPaymentMethod)
    }
  }, [initialSelectedPaymentMethod])

  const [addPaymentMethodAccountType, setAddPaymentMethodAccountType] =
    useState<PaymentMethodTypesEnum | null>(null)

  const handleOnRetry = async (): Promise<ApolloQueryResult<LoanPayMethodsQuery>> => {
    return queryResult.refetch()
  }

  const handleOnSelectExistingPaymentMethod = (paymentMethod: LoanPaymentMethod): void => {
    setAddPaymentMethodAccountType(null)
    setSelectedPaymentMethod(paymentMethod)
    setSelectedPaymentMethodId(paymentMethod.bankingPaymentInstrumentId)
    TrackAppEvent(
      ManageActiveLoanEvents.make_payment_methods_change_payment_method,
      AppEvents.Category.ManageActiveLoan,
      {
        type: 'existing_account',
      },
    )
  }

  const handleOnSelectAddNewPaymentMethod = (accountType: PaymentMethodTypesEnum): void => {
    // unselect the current payment method on the screen
    setSelectedPaymentMethod(null)
    // set the new payment method type
    setAddPaymentMethodAccountType(accountType)

    // Reset the payment method id to the original selected payment method for now, in case user presses back to cancel
    setSelectedPaymentMethodId(initialSelectedPaymentMethod?.bankingPaymentInstrumentId)
  }

  const handleOnAddNewAccount = async (type: PaymentMethodTypesEnum): Promise<void> => {
    // the remaining switch case is for PaymentMethodTypesEnum is Yodlee
    // which is not being used for adding new accounts
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (type) {
      case PaymentMethodTypesEnum.DebitCard:
        TrackAppEvent(
          ManageActiveLoanEvents.make_payment_methods_change_payment_method,
          AppEvents.Category.ManageActiveLoan,
          {
            type: getPaymentMethodEventTypeFromEnum(type),
          },
        )
        navigation.navigate('CollectDebitCardNumberForMakePayment', {
          onSuccessRouteDestination: 'LoanPayConfirm',
        })
        break
      case PaymentMethodTypesEnum.AccountRouting:
        TrackAppEvent(
          ManageActiveLoanEvents.make_payment_methods_change_payment_method,
          AppEvents.Category.ManageActiveLoan,
          {
            type: getPaymentMethodEventTypeFromEnum(type),
          },
        )
        navigation.navigate('BankAddRoutingAndAccount', {
          onSuccessRouteDestination: 'LoanPayConfirm',
        })
        break
      case PaymentMethodTypesEnum.Plaid:
        TrackAppEvent(
          ManageActiveLoanEvents.make_payment_methods_change_payment_method,
          AppEvents.Category.ManageActiveLoan,
          {
            type: getPaymentMethodEventTypeFromEnum(type),
          },
        )
        await openBankAggregator({
          selectAndVerifyAccountMode: 'ForMakePayment',
          onBankLinkComplete: ({newAchPaymentMethod}) => {
            if (!newAchPaymentMethod) {
              logAddPaymentMethodError(
                new Error(
                  `LoanPayMethods for routeName="${'LoanPayConfirm'}" finished bank linking with aggregator but did not receive an achPaymentMethod`,
                ),
              )
            }

            // when done linking account w/plaid we'll go back to the screen before adding the new payment method with the new payment method selected
            navigation.navigate('LoanPayConfirm', {
              newSelectedPaymentInstrumentId: newAchPaymentMethod?.id,
            })
          },
        })
        break
      default:
        break
    }
  }

  const handleOnContinue = async (): Promise<void> => {
    // if we're adding a new payment method, we need to add it
    if (addPaymentMethodAccountType !== null) {
      await handleOnAddNewAccount(addPaymentMethodAccountType)
      TrackAppEvent(
        ManageActiveLoanEvents.make_payment_methods_change_payment_method_completed,
        AppEvents.Category.ManageActiveLoan,
        {
          type: getPaymentMethodEventTypeFromEnum(addPaymentMethodAccountType),
        },
      )
    } else if (selectedPaymentMethod?.bankingPaymentInstrumentId) {
      TrackAppEvent(
        ManageActiveLoanEvents.make_payment_methods_change_payment_method_completed,
        AppEvents.Category.ManageActiveLoan,
        {
          type: 'existing_account',
        },
      )
      navigation.navigate('LoanPayConfirm')
    }
  }

  const handleOnErrorBoundary = (e: Error): void => {
    trackLoansMakePaymentScreenRenderFailure(e, 'LoanPayMethods')
  }

  return (
    <BaseTemplate
      isLoading={queryResult.loading && !selectedPaymentMethod && !queryResult.error}
      isError={!!queryResult.error}
      testID={'LoanPayMethods'}
      onRetry={handleOnRetry}
      transparentBackground={true}
      onErrorBoundary={handleOnErrorBoundary}
      trackError={{
        eventName: ManageActiveLoanEvents.make_payment_methods_screen_error_viewed,
        eventCategory: AppEvents.Category.ManageActiveLoan,
      }}
    >
      <ChaosEnabled dispatch={dispatch} componentName={'LoanPayMethods'}>
        <ViewPaymentMethodsTemplate
          achPaymentMethods={achPaymentMethods}
          canAddMakePaymentInstrument={
            queryResult.selectedData?.canAddMakePaymentInstrument ?? false
          }
          debitPaymentMethods={debitPaymentMethods}
          hideAggregatorButton={isYodleeAggregator ?? false}
          addPaymentMethodAccountType={addPaymentMethodAccountType}
          onContinue={handleOnContinue}
          onSelectAddNewPaymentMethod={handleOnSelectAddNewPaymentMethod}
          onSelectExistingPaymentMethod={handleOnSelectExistingPaymentMethod}
          selectedPaymentMethod={selectedPaymentMethod ?? null}
        />
      </ChaosEnabled>
    </BaseTemplate>
  )
}

export {LoanPayMethods}
